import {
    Component,
    EventEmitter,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import {
    ColDef,
    GridApi,
    GridOptions,
    GridReadyEvent,
    ICellRendererParams,
    IRowNode,
    IServerSideDatasource,
    IServerSideGetRowsRequest,
} from 'ag-grid-community';
import { first, forkJoin, of, take } from 'rxjs';

import { RouterService } from '@services/router.service';
import { UserService } from '@services/api/user/user.service';
import { UserDataService } from '@services/data/user-data.service';
import { StationColumnOptionsService } from '@services/station-column-options.service';
import { StationDiscrepancyService } from '@services/api/station-discrepancy/station-discrepancy.service';
import { WorkflowService } from '@services/api/workflow/workflow.service';
import { TableComponent } from '@shared/table/table.component';
import { TextFilterComponent } from '@grid-table/filter/text-filter/text-filter.component';
import { SelectFilterComponent } from '@grid-table/filter/select-filter/select-filter.component';
import { PriorityCellComponent } from '@grid-table/cell/priority-cell/priority-cell.component';
import { DateFilterComponent } from '@grid-table/filter/date-filter/date-filter.component';
import { GrayBoxCellComponent } from '@grid-table/cell/gray-box-cell/gray-box-cell.component';
import { RangeFilterComponent } from '@grid-table/filter/range-filter/range-filter.component';
import { ClickableCellComponent } from '@grid-table/cell/clickable-cell/clickable-cell.compoent';
import { CorrectionCellComponent } from '@grid-table/cell/correction-cell/correction-cell.component';
import { NotificationCellComponent } from '@grid-table/cell/notification-cell/notification-cell.component';
import { DocumentNameCellComponent } from '@grid-table/cell/document-name-cell/document-name-cell.component';
import { ExportFileNames } from '@constants/table';
import { IdDisplayPair } from '@models/shared.model';
import { CountInformation } from '@models/shared/count-information';
import { Range } from '@models/shared/range';
import {
    StationDiscrepancy,
    DiscrepancyFilterOptions,
} from '@models/station-discrepancy';
import { CustomColumn } from '@models/workflow/custom-column';
import { SubscriptionList, SubscriptionListType } from '@helpers/subscription';
import { pickFromEnum } from '@helpers/index';
import {
    getFilterQuery,
    getPageIndex,
    getSortQuery,
    hideHiddenColumns,
    isFilterApplied,
    setFilterAndSorting,
} from '@helpers/table';
import { customColumnsToColDefs } from '@helpers/custom-column';
import {
    InboxTab,
    DiscrepancyColumn,
    getDaysSinceNotification,
} from '@station/utils';
import DATA_QA from '@automation/data-qa.json';

@Component({
    selector: 'app-station-discrepancy-view',
    templateUrl: 'discrepancy-view.component.html',
    imports: [TableComponent],
})
export class StationDiscrepancyViewComponent
    implements OnInit, OnChanges, OnDestroy
{
    @Input() inboxTab: InboxTab | null = null;
    @Input() workflowId: number | undefined;
    @Output() customColumnsChange = new EventEmitter<CustomColumn[]>();

    filterApplied: boolean = false;
    countInformation = CountInformation.initial();
    lastSyncDate: Date | undefined;
    protected error = false;
    protected readonly dataQa = DATA_QA;
    protected columnDefs: ColDef[] = [];
    protected gridOptions!: GridOptions;
    private _gridApi!: GridApi;
    private hiddenDiscColumns: Set<string> = new Set([]);
    private customColumns: CustomColumn[] = [];
    private currentFilterOptions: DiscrepancyFilterOptions =
        DiscrepancyFilterOptions.initial();
    private _subscriptions = new SubscriptionList() as SubscriptionListType;

    private readonly userService = inject(UserService);
    private readonly routerService = inject(RouterService);
    private readonly userDataService = inject(UserDataService);
    private readonly discrepancyService = inject(StationDiscrepancyService);
    private readonly columnOptionsService = inject(StationColumnOptionsService);
    private readonly workflowService = inject(WorkflowService);

    ngOnInit(): void {
        this.setGridOptions();
        this.setColumnDefs();
        this.handleColumnOptions();
        this.loadColumnsAndFilters();
    }

    ngOnChanges(changes: SimpleChanges): void {
        const inboxTabChange = changes['inboxTab'];
        const workflowIdChange = changes['workflowId'];

        if (workflowIdChange && !workflowIdChange.firstChange) {
            this.resetFilters();
            this.loadColumnsAndFilters(true, true);
        } else if (inboxTabChange && !inboxTabChange.firstChange) {
            this.resetFilters();
            this.getFilterOptions(true);
        }
    }

    ngOnDestroy() {
        this._subscriptions['get-inbox-choices'] = this.userService
            .getStationInboxChoices()
            .subscribe({
                complete: () => {
                    this._subscriptions.unsubscribeAllSafe();
                },
            });
    }

    setGridOptions() {
        const date = new Date().toUTCString();
        const fileNameToExport = `${ExportFileNames.INBOX} - ${date}`;

        this.gridOptions = {
            enableRangeSelection: true,
            defaultExcelExportParams: { fileName: fileNameToExport },
            defaultCsvExportParams: { fileName: fileNameToExport },
        };
    }

    setColumnDefs(filterOptions = DiscrepancyFilterOptions.initial()) {
        this.currentFilterOptions = filterOptions;
        this.columnDefs = [...this.getColumnDefs(filterOptions)];
    }

    onGridReady(event: GridReadyEvent): void {
        this._gridApi = event.api;

        const datasource = this.getAllDiscrepancies();
        this._gridApi.setGridOption('serverSideDatasource', datasource);
        this.applySavedColumnVisibility();
    }

    resetFilters() {
        if (!this._gridApi.isDestroyed()) {
            this._gridApi?.refreshHeader();
            this._gridApi?.setFilterModel(null);
            this._gridApi?.onFilterChanged();
        }
    }

    refreshData(purge = true) {
        if (!this._gridApi.isDestroyed()) {
            if (this.error) {
                const datasource = this.getAllDiscrepancies();
                this._gridApi.setGridOption('serverSideDatasource', datasource);
            } else {
                this._gridApi.refreshServerSide({ purge });
            }
        }
    }

    private getColumnDefs(filterOptions: DiscrepancyFilterOptions): ColDef[] {
        return [
            {
                field: DiscrepancyColumn.ID,
                maxWidth: 140,
                headerName: 'Discrepancy ID',
                filter: 'agTextColumnFilter',
                cellRenderer: ClickableCellComponent,
                cellRendererParams: {
                    dataQa: this.dataQa.stationDiscrepancyViewDiscrepancyIdCell,
                    onClick: (node: IRowNode) =>
                        this.onDiscrepancyIdClick(node),
                },
                floatingFilterComponent: TextFilterComponent,
                floatingFilterComponentParams: {
                    type: 'number',
                },
            },
            {
                field: DiscrepancyColumn.DOC_NAME,
                headerName: 'WF Document Name',
                minWidth: 180,
                filter: 'agTextColumnFilter',
                cellRenderer: DocumentNameCellComponent,
                cellRendererParams: {
                    tooltipGetter: (params: ICellRendererParams) =>
                        params.data.documentName,
                },
                floatingFilterComponent: TextFilterComponent,
            },
            {
                field: DiscrepancyColumn.NOTIFICATION,
                filter: 'agTextColumnFilter',
                minWidth: 170,
                cellRenderer: NotificationCellComponent,
                floatingFilterComponent: SelectFilterComponent,
                floatingFilterComponentParams: {
                    suppressFloatingFilterButton: true,
                    selectList: filterOptions.notification,
                },
            },
            {
                field: DiscrepancyColumn.CORRECTION,
                filter: 'agTextColumnFilter',
                minWidth: 170,
                cellRenderer: CorrectionCellComponent,
                floatingFilterComponent: SelectFilterComponent,
                floatingFilterComponentParams: {
                    suppressFloatingFilterButton: true,
                    selectList: filterOptions.correction,
                },
            },
            {
                field: DiscrepancyColumn.REASON,
                minWidth: 130,
                valueFormatter: (p: { value: IdDisplayPair }) =>
                    p.value?.displayName ?? '-',
                filter: 'agTextColumnFilter',
                floatingFilterComponent: SelectFilterComponent,
                floatingFilterComponentParams: {
                    suppressFloatingFilterButton: true,
                    selectList: filterOptions.reason,
                    autocomplete: true,
                    noneOption: true,
                },
            },
            {
                field: DiscrepancyColumn.PRIORITY,
                maxWidth: 130,
                cellRenderer: PriorityCellComponent,
                filter: 'agTextColumnFilter',
                floatingFilterComponent: SelectFilterComponent,
                floatingFilterComponentParams: {
                    suppressFloatingFilterButton: true,
                    selectList: filterOptions.priority,
                    priority: true,
                },
            },
            {
                field: DiscrepancyColumn.NOTIFY_DATE,
                filter: 'agTextColumnFilter',
                maxWidth: 170,
                floatingFilterComponent: DateFilterComponent,
            },
            {
                field: DiscrepancyColumn.DAYS_SINCE_NOTIFICATION,
                headerName: 'Days Notified',
                maxWidth: 120,
                cellRenderer: GrayBoxCellComponent,
                valueFormatter: getDaysSinceNotification,
                cellStyle: { textOverflow: 'clip' },
                filter: 'agTextColumnFilter',
                floatingFilterComponent: RangeFilterComponent,
                floatingFilterComponentParams: {
                    availableRange: new Range(
                        0,
                        filterOptions.maxDaysSinceNotification,
                    ),
                    title: 'Filter by Days Notified',
                    inputLabel: 'days',
                },
            },
            {
                field: DiscrepancyColumn.STATION,
                headerName: 'Station',
                minWidth: 120,
                filter: 'agTextColumnFilter',
                floatingFilterComponent: SelectFilterComponent,
                floatingFilterComponentParams: {
                    suppressFloatingFilterButton: true,
                    selectList: filterOptions.station,
                    autocomplete: true,
                    noneOption: true,
                },
            },
            {
                field: DiscrepancyColumn.PRIMARY_FIXER,
                minWidth: 180,
                filter: 'agTextColumnFilter',
                floatingFilterComponent: SelectFilterComponent,
                floatingFilterComponentParams: {
                    suppressFloatingFilterButton: true,
                    selectList: filterOptions.primaryFixer,
                    autocomplete: true,
                },
            },
            ...customColumnsToColDefs(
                this.customColumns,
                Array.from(this.hiddenDiscColumns),
                filterOptions.customColumns,
            ),
        ];
    }

    private onDiscrepancyIdClick(node: IRowNode<StationDiscrepancy>) {
        if (node.data) {
            this.routerService.goStationDocumentDetails(
                node.data.documentId,
                node.data.id,
            );
        }
    }

    private getAllDiscrepancies(): IServerSideDatasource {
        return {
            getRows: (params) => {
                params.api.hideOverlay();
                this.filterApplied = isFilterApplied(params.request);
                if (this.filterApplied) this._gridApi.deselectAll();

                const queryParams = this.getQueryParams(params.request);
                this.getTotalDiscCount(queryParams);

                this.discrepancyService
                    .getAllDiscrepancies(queryParams)
                    .pipe(first())
                    .subscribe({
                        next: (data) => {
                            this.lastSyncDate = new Date();
                            params.api.hideOverlay();

                            if (data) {
                                params.success({
                                    rowData: data.items,
                                    rowCount: data.totalItems ?? -1,
                                });
                                if (data.items.length === 0) {
                                    params.api.showNoRowsOverlay();
                                }
                            } else {
                                params.fail();
                            }
                        },
                        error: () => {
                            params.api.hideOverlay();
                            this.error = true;
                            this.lastSyncDate = undefined;
                        },
                    });
            },
        };
    }

    private getTotalDiscCount(params: { pageIndex: number; pageSize: number }) {
        const { pageIndex, pageSize, ...rest } = params;

        this.discrepancyService
            .getStationDiscrepanciesCount(rest)
            .pipe(take(1))
            .subscribe({
                next: (response) => {
                    if (response) {
                        this.countInformation = response;
                    }
                },
            });
    }

    private handleColumnOptions() {
        this._subscriptions['column-options'] =
            this.columnOptionsService.discColumnOptions$.subscribe({
                next: (options) => {
                    this.hiddenDiscColumns = new Set(options.hiddenColumns);
                    const option = options.lastToggled;

                    if (
                        option &&
                        this._gridApi &&
                        !this._gridApi.isDestroyed()
                    ) {
                        // Single user toggle: apply imperatively (no flicker).
                        this._gridApi.setColumnsVisible(
                            [option.id],
                            option.checked,
                        );
                    } else if (this._gridApi && !this._gridApi.isDestroyed()) {
                        // Restore path (no lastToggled, e.g. saved config sync):
                        // rebuild columnDefs so custom columns' declarative `hide`
                        // matches the saved hidden state. ag-Grid re-applies colDef
                        // `hide` on every columnDefs update, which would otherwise
                        // re-show hidden custom columns (e.g. ecnDate) after refresh.
                        this.setColumnDefs(this.currentFilterOptions);
                        this._gridApi.setGridOption(
                            'columnDefs',
                            this.columnDefs,
                        );
                    }

                    setTimeout(() => this.applySavedColumnVisibility());
                },
            });
    }

    // Load custom columns AND filter options together so the final columnDefs
    // (static + custom) are pushed to ag-Grid BEFORE setSavedFilter runs.
    // This ensures custom column colIds are registered before setFilterAndSorting
    // tries to restore filter/sort state.
    private loadColumnsAndFilters(
        ignoreUserChoices = false,
        refreshAfter = false,
    ) {
        const customColumns$ =
            this.workflowId !== undefined && this.workflowId !== null
                ? this.workflowService.getCustomColumns(this.workflowId)
                : of<CustomColumn[]>([]);
        const filterOptions$ =
            this.discrepancyService.getDiscrepanciesFilterOptions(
                this.buildBaseParams(),
            );

        this._subscriptions['load-columns-and-filters'] = forkJoin({
            customColumns: customColumns$,
            filterOptions: filterOptions$,
        }).subscribe({
            next: ({ customColumns, filterOptions }) => {
                this.customColumns = customColumns;
                this.customColumnsChange.emit(customColumns);

                this.setColumnDefs(filterOptions);
                // Push columnDefs directly to ag-Grid so all columns are
                // registered before setSavedFilter applies filter/sort state;
                // the Angular `[columnDefs]` binding does not propagate
                // synchronously.
                if (this._gridApi && !this._gridApi.isDestroyed()) {
                    this._gridApi.setGridOption('columnDefs', this.columnDefs);
                    setTimeout(() => this.applySavedColumnVisibility());
                }
                if (refreshAfter) this.refreshData();
            },
            complete: () => {
                if (!ignoreUserChoices) this.setSavedFilter();
            },
        });
    }

    private getFilterOptions(ignoreUserChoices = false) {
        this.discrepancyService
            .getDiscrepanciesFilterOptions(this.buildBaseParams())
            .pipe(first())
            .subscribe({
                next: (response: DiscrepancyFilterOptions) => {
                    this.setColumnDefs(response);
                    setTimeout(() => this.applySavedColumnVisibility());
                },
                complete: () => {
                    if (!ignoreUserChoices) this.setSavedFilter();
                },
            });
    }

    private buildBaseParams(): Record<string, any> {
        const params: Record<string, any> = {
            inboxTab: this.inboxTab ?? undefined,
        };
        if (this.workflowId !== undefined && this.workflowId !== null) {
            params['workflowId'] = this.workflowId;
        }
        return params;
    }

    private setSavedFilter() {
        this._subscriptions['set-saved-filter'] =
            this.userDataService.stationUserInboxChoices$.subscribe({
                next: (choices) => {
                    if (choices) {
                        // Static column filters via pickFromEnum (unchanged).
                        const staticFilters = pickFromEnum(
                            choices.discrepancy,
                            {
                                ...DiscrepancyColumn,
                                order: 'order',
                                orderBy: 'orderBy',
                            },
                        );

                        // Custom column filters: keys from BE may be any casing
                        // (e.g. "ecnDate") but ag-Grid colIds are always lowercase.
                        // Extract and normalize them separately.
                        const customColumnNameSet = new Set(
                            this.customColumns.map((c) =>
                                c.columnName.toLowerCase(),
                            ),
                        );
                        const customFilters = Object.fromEntries(
                            Object.entries(
                                choices.discrepancy as Record<string, any>,
                            )
                                .filter(([key]) =>
                                    customColumnNameSet.has(key.toLowerCase()),
                                )
                                .map(([key, value]) => [
                                    key.toLowerCase(),
                                    value,
                                ]),
                        );

                        // Also normalize orderBy if it refers to a custom column.
                        const orderBy = staticFilters['orderBy'];
                        if (
                            orderBy &&
                            customColumnNameSet.has(
                                (orderBy as string).toLowerCase(),
                            )
                        ) {
                            staticFilters['orderBy'] = (
                                orderBy as string
                            ).toLowerCase();
                        }

                        setFilterAndSorting(this._gridApi, {
                            ...staticFilters,
                            ...customFilters,
                        });
                        this.columnOptionsService.syncColumnConfig(
                            choices.columnConfig,
                        );
                    }
                },
            });
    }

    private getQueryParams(request: IServerSideGetRowsRequest) {
        const pageSize = this._gridApi.paginationGetPageSize();
        const pageIndex = getPageIndex(request, pageSize);

        return {
            ...this.buildBaseParams(),
            ...getFilterQuery(request.filterModel),
            ...getSortQuery(request.sortModel),
            pageIndex,
            pageSize,
        };
    }

    private applySavedColumnVisibility() {
        hideHiddenColumns(this._gridApi, Array.from(this.hiddenDiscColumns));
    }
}
